import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ApexChart from "react-apexcharts";
import "./dashboard-weekly-sales.css";

/**
 * Figma section below hero cards:
 * Sales (grouped Cash/Card/Online bars) + Order Count list.
 * Series/categories from GetTenderSalesForChart; order counts from SalesKpis.
 */

const CHART_HEIGHT_DESKTOP = 248;
const CHART_HEIGHT_TABLET = 220;
const CHART_HEIGHT_MOBILE = 200;

function useResponsiveChartHeight() {
  const [height, setHeight] = useState(CHART_HEIGHT_DESKTOP);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mqTablet = window.matchMedia("(max-width: 991.98px)");
    const mqMobile = window.matchMedia("(max-width: 575.98px)");

    const apply = () => {
      if (mqMobile.matches) setHeight(CHART_HEIGHT_MOBILE);
      else if (mqTablet.matches) setHeight(CHART_HEIGHT_TABLET);
      else setHeight(CHART_HEIGHT_DESKTOP);
    };

    apply();
    mqTablet.addEventListener("change", apply);
    mqMobile.addEventListener("change", apply);
    return () => {
      mqTablet.removeEventListener("change", apply);
      mqMobile.removeEventListener("change", apply);
    };
  }, []);

  return height;
}

const EMPTY_WEEKLY = {
  categories: [],
  cash: [],
  card: [],
  online: [],
  grossSale: 0,
  bucket: "day",
};

const PLACEHOLDER_ORDERS = {
  total: 0,
  completed: 0,
  cancelled: 0,
  refunds: 0,
  running: 0,
};

const SERIES_META = [
  {
    key: "cash",
    name: "Cash",
    colorFrom: "#30D6A4",
    colorTo: "#197056",
    border: "#3AD6A5",
    fillFrom: "#3AD6A5",
    fillTo: "#1F755A",
  },
  {
    key: "card",
    name: "Card",
    colorFrom: "#5B8DEF",
    colorTo: "#2F5BB8",
    border: "#1C8DE7",
    fillFrom: "#1775EB",
    fillTo: "#0D4C99",
  },
  {
    key: "online",
    name: "Online",
    colorFrom: "#E8ECF0",
    colorTo: "#9AA3AD",
    border: "#C0C2C4",
    fillFrom: "#E8ECF0",
    fillTo: "#8B9094",
  },
];

const BUCKET_COPY = {
  hour: { title: "Sales", subtitle: "By hour" },
  day: { title: "Sales", subtitle: "By day" },
  week: { title: "Sales", subtitle: "By week" },
};

function formatMoney(value, symbol = "£") {
  const n = Number(value) || 0;
  return `${symbol} ${n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatTipMoney(value, symbol = "£") {
  const n = Number(value) || 0;
  return `${symbol}${n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function DashboardWeeklySales({
  weekly = EMPTY_WEEKLY,
  orders = PLACEHOLDER_ORDERS,
  currencySymbol = "£",
}) {
  const chartWrapRef = useRef(null);
  /** Tracks "seriesIndex:dataPointIndex" so tip updates when moving between bars in the same day/hour. */
  const tipKeyRef = useRef("");
  const [tip, setTip] = useState(null);
  const chartHeight = useResponsiveChartHeight();

  const isDark =
    typeof document !== "undefined" &&
    document.documentElement.getAttribute("data-bs-theme") === "dark";

  const axisColor = isDark ? "rgba(255,255,255,0.55)" : "#64748b";
  const gridColor = isDark ? "rgba(255,255,255,0.1)" : "rgba(15,23,42,0.08)";

  const categories = useMemo(() => {
    if (Array.isArray(weekly.categories) && weekly.categories.length > 0) {
      return weekly.categories;
    }
    const len = Math.max(
      weekly.cash?.length || 0,
      weekly.card?.length || 0,
      weekly.online?.length || 0,
      1
    );
    return Array.from({ length: len }, (_, i) => String(i + 1));
  }, [weekly]);

  const bucketKey = String(weekly.bucket || "day").toLowerCase();
  const isHourBucket = bucketKey === "hour";
  const categoryCount = categories.length;
  // Hour mode: 24 bars — show every 2nd label (readable); day/week keep prior rules.
  const rotateLabels = isHourBucket ? false : categoryCount > 10;
  const denseLabels = isHourBucket ? false : categoryCount > 16;
  const columnWidth = isHourBucket
    ? "58%"
    : categoryCount <= 7
      ? "48%"
      : categoryCount <= 16
        ? "55%"
        : "62%";
  const titleCopy = BUCKET_COPY[bucketKey] || BUCKET_COPY.day;

  const series = useMemo(
    () =>
      SERIES_META.map((s) => ({
        name: s.name,
        data: Array.isArray(weekly[s.key])
          ? weekly[s.key].map((v) => Number(v) || 0)
          : categories.map(() => 0),
      })),
    [weekly, categories]
  );

  const hideTip = useCallback(() => {
    tipKeyRef.current = "";
    setTip(null);
  }, []);

  /**
   * Per-bar tip: Cash / Card / Online value for the hovered bar (not column total).
   * Positions the Figma bubble above that bar when the SVG path is found.
   */
  const showTipForBar = useCallback(
    (seriesIndex, dataPointIndex, event) => {
      const wrap = chartWrapRef.current;
      const n = categories.length;
      const seriesCount = series.length;
      if (
        !wrap ||
        n <= 0 ||
        seriesIndex < 0 ||
        seriesIndex >= seriesCount ||
        dataPointIndex < 0 ||
        dataPointIndex >= n
      ) {
        return;
      }

      const meta = SERIES_META[seriesIndex] || SERIES_META[0];
      const value = Number(series[seriesIndex]?.data?.[dataPointIndex]) || 0;
      const category =
        categories[dataPointIndex] || (isHourBucket ? "Hour" : "Total Sales");
      const tipKey = `${seriesIndex}:${dataPointIndex}`;

      const wrapRect = wrap.getBoundingClientRect();
      let left = null;
      let top = null;

      // Prefer the actual bar path so tip sits on Cash vs Card vs Online correctly.
      const seriesNodes = wrap.querySelectorAll(
        ".apexcharts-bar-series .apexcharts-series"
      );
      const seriesNode = seriesNodes[seriesIndex];
      const barPaths = seriesNode
        ? seriesNode.querySelectorAll("path.apexcharts-bar-area, path")
        : [];
      const barEl = barPaths[dataPointIndex];
      if (barEl && typeof barEl.getBoundingClientRect === "function") {
        const barRect = barEl.getBoundingClientRect();
        if (barRect.width > 0 || barRect.height > 0) {
          left = barRect.left - wrapRect.left + barRect.width / 2;
          top = barRect.top - wrapRect.top;
        }
      }

      // Fallback: split category column into equal series bands (Cash | Card | Online).
      if (left == null || top == null) {
        const gridEl =
          wrap.querySelector(".apexcharts-grid") ||
          wrap.querySelector(".apexcharts-inner");
        if (!gridEl || typeof gridEl.getBoundingClientRect !== "function") {
          return;
        }
        const gridRect = gridEl.getBoundingClientRect();
        const colW = gridRect.width / n;
        const bandW = colW / seriesCount;
        left =
          gridRect.left -
          wrapRect.left +
          dataPointIndex * colW +
          (seriesIndex + 0.5) * bandW;
        top = gridRect.top - wrapRect.top + 12;
        if (event?.clientY != null) {
          top = Math.min(
            top,
            Math.max(8, event.clientY - wrapRect.top - 8)
          );
        }
      }

      const nextTip = {
        left,
        top,
        value,
        seriesName: meta.name,
        seriesKey: meta.key,
        category,
      };

      if (tipKeyRef.current === tipKey) {
        // Still refresh position if the chart reflowed under the same bar.
        setTip((prev) =>
          prev &&
          prev.left === left &&
          prev.top === top &&
          prev.seriesKey === meta.key
            ? prev
            : nextTip
        );
        return;
      }

      tipKeyRef.current = tipKey;
      setTip(nextTip);
    },
    [categories, series, isHourBucket]
  );

  /** Resolve series + category from pointer X when Apex does not fire dataPoint events (zero-height bars). */
  const showTipFromPointer = useCallback(
    (event) => {
      const wrap = chartWrapRef.current;
      const n = categories.length;
      const seriesCount = series.length;
      if (!wrap || !event || n <= 0 || seriesCount <= 0) return;

      const gridEl =
        wrap.querySelector(".apexcharts-grid") ||
        wrap.querySelector(".apexcharts-inner");
      if (!gridEl || typeof gridEl.getBoundingClientRect !== "function") return;

      const gridRect = gridEl.getBoundingClientRect();
      const { clientX, clientY } = event;

      if (
        clientX < gridRect.left ||
        clientX > gridRect.right ||
        clientY < gridRect.top ||
        clientY > gridRect.bottom
      ) {
        hideTip();
        return;
      }

      const relX = clientX - gridRect.left;
      const dataPointIndex = Math.min(
        n - 1,
        Math.max(0, Math.floor((relX / gridRect.width) * n))
      );
      const colW = gridRect.width / n;
      const withinCol = (relX - dataPointIndex * colW) / colW;
      const seriesIndex = Math.min(
        seriesCount - 1,
        Math.max(0, Math.floor(withinCol * seriesCount))
      );

      showTipForBar(seriesIndex, dataPointIndex, event);
    },
    [categories.length, series.length, hideTip, showTipForBar]
  );

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        stacked: false,
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: "inherit",
        background: "transparent",
        width: "100%",
        parentHeightOffset: 0,
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
        animations: { enabled: true, speed: 500 },
        events: {
          dataPointMouseEnter(_event, _ctx, config) {
            const seriesIndex = Number(config?.seriesIndex);
            const dataPointIndex = Number(config?.dataPointIndex);
            if (
              Number.isFinite(seriesIndex) &&
              Number.isFinite(dataPointIndex)
            ) {
              showTipForBar(seriesIndex, dataPointIndex, _event);
            }
          },
          // Do not hide on dataPointMouseLeave — moving Cash→Card would flicker; chart mouseLeave clears.
          mouseMove(_event, _ctx, config) {
            // Prefer Apex hit-test when over a real bar path.
            const seriesIndex = Number(config?.seriesIndex);
            const dataPointIndex = Number(config?.dataPointIndex);
            if (
              Number.isFinite(seriesIndex) &&
              seriesIndex >= 0 &&
              Number.isFinite(dataPointIndex) &&
              dataPointIndex >= 0
            ) {
              showTipForBar(seriesIndex, dataPointIndex, _event);
              return;
            }
            // Fallback for gaps / zero-height bars: map X into category × series band.
            const ev = _event?.clientX != null ? _event : config;
            if (ev?.clientX != null) showTipFromPointer(ev);
          },
          mouseLeave() {
            hideTip();
          },
          updated() {
            hideTip();
          },
        },
      },
      colors: SERIES_META.map((s) => s.colorFrom),
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth,
          borderRadius: 6,
          borderRadiusApplication: "end",
          borderRadiusWhenStacked: "last",
        },
      },
      states: {
        hover: {
          filter: { type: "lighten", value: 0.08 },
        },
        active: {
          allowMultipleDataPointsSelection: false,
          filter: { type: "none" },
        },
      },
      dataLabels: { enabled: false },
      stroke: { show: true, width: 2, colors: ["transparent"] },
      fill: {
        type: "gradient",
        gradient: {
          shade: "dark",
          type: "vertical",
          shadeIntensity: 0.35,
          gradientToColors: SERIES_META.map((s) => s.colorTo),
          inverseColors: false,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 100],
        },
      },
      xaxis: {
        categories,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          rotate: rotateLabels ? -45 : 0,
          rotateAlways: rotateLabels,
          hideOverlappingLabels: denseLabels,
          trim: denseLabels,
          // Single-day (hour) chart: label every 2 hours so the axis stays readable;
          // hover tip still uses the full hour label for every bar.
          formatter: (value, _ts, opts) => {
            if (!isHourBucket) return value;
            const i =
              typeof opts?.i === "number"
                ? opts.i
                : categories.indexOf(value);
            if (i < 0) return value;
            return i % 2 === 0 ? value : "";
          },
          style: {
            colors: axisColor,
            fontSize: isHourBucket ? "10px" : denseLabels ? "10px" : "12px",
            fontWeight: 500,
          },
        },
        // Full-height column guide while hovering a day/hour/week band
        crosshairs: {
          show: true,
          width: 1,
          position: "back",
          opacity: 0.35,
          stroke: {
            color: "#3AD6A5",
            width: 1,
            dashArray: 4,
          },
        },
        tooltip: {
          enabled: false,
        },
      },
      yaxis: {
        labels: {
          style: { colors: axisColor, fontSize: "11px" },
          formatter: (v) => {
            if (v >= 1000) return `${currencySymbol}${Math.round(v / 1000)}K`;
            return `${currencySymbol}${v}`;
          },
        },
      },
      grid: {
        borderColor: gridColor,
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: {
          left: 4,
          right: 4,
          top: 52,
          bottom: rotateLabels ? 8 : 0,
        },
      },
      legend: { show: false },
      tooltip: { enabled: false },
      markers: { size: 0 },
    }),
    [
      axisColor,
      gridColor,
      currencySymbol,
      showTipForBar,
      showTipFromPointer,
      hideTip,
      categories,
      columnWidth,
      rotateLabels,
      denseLabels,
      isHourBucket,
    ]
  );

  const orderRows = [
    { key: "completed", label: "Completed", value: orders.completed },
    { key: "cancelled", label: "Cancelled", value: orders.cancelled },
    { key: "refunds", label: "Refunds", value: orders.refunds },
    { key: "running", label: "Running", value: orders.running },
  ];

  return (
    <section className="dashboard-weekly" aria-label="Sales and order count">
      <div className="dashboard-weekly__row">
        <article className="dashboard-weekly__card dashboard-weekly__sales">
          <div className="dashboard-weekly__sales-head">
            <div className="dashboard-weekly__titles">
              <h2 className="dashboard-weekly__title">{titleCopy.title}</h2>
              <p className="dashboard-weekly__subtitle">{titleCopy.subtitle}</p>
            </div>

            <div className="dashboard-weekly__legend" aria-label="Series legend">
              {SERIES_META.map((s) => (
                <span key={s.key} className="dashboard-weekly__legend-item">
                  <span
                    className={`dashboard-weekly__legend-swatch dashboard-weekly__legend-swatch--${s.key}`}
                    style={{ borderColor: s.border }}
                    aria-hidden="true"
                  >
                    <span
                      className="dashboard-weekly__legend-swatch-inner"
                      style={{
                        background: `linear-gradient(180deg, ${s.fillFrom} 0%, ${s.fillTo} 100%)`,
                      }}
                    />
                  </span>
                  {s.name}
                </span>
              ))}
            </div>

            <div className="dashboard-weekly__gross">
              <div className="dashboard-weekly__gross-value">
                {formatMoney(weekly.grossSale, currencySymbol)}
              </div>
              <div className="dashboard-weekly__gross-label">Total Sales</div>
            </div>
          </div>

          <div className="dashboard-weekly__chart" ref={chartWrapRef}>
            <ApexChart
              type="bar"
              height={chartHeight}
              width="100%"
              series={series}
              options={chartOptions}
            />

            {tip && (
              <div
                className="dws-tip-anchor"
                style={{ left: tip.left, top: tip.top }}
                role="tooltip"
              >
                {/* Figma: white bubble — "Total Sales" + green £ — arrow — white ring on bar */}
                <div className="dws-tip">
                  <div className="dws-tip__label">Total Sales</div>
                  <div className="dws-tip__value">
                    {formatTipMoney(tip.value, currencySymbol)}
                  </div>
                </div>
                <span className="dws-tip__arrow" aria-hidden="true" />
                <span className="dws-tip__marker" aria-hidden="true" />
              </div>
            )}
          </div>
        </article>

        <article className="dashboard-weekly__card dashboard-weekly__orders">
          <div className="dashboard-weekly__orders-head">
            <h2 className="dashboard-weekly__title">Order Count</h2>
            <div className="dashboard-weekly__orders-total">{orders.total}</div>
          </div>
          <ul className="dashboard-weekly__order-list">
            {orderRows.map((row) => (
              <li key={row.key} className="dashboard-weekly__order-row">
                <span className="dashboard-weekly__order-pill">
                  <span className="dashboard-weekly__order-num">
                    {pad2(row.value)}
                  </span>
                  <span className="dashboard-weekly__order-label">
                    {row.label}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </article>
      </div>
    </section>
  );
}

export default DashboardWeeklySales;
